// Loan Compliance Module v1.0 PR 3 — raw HTTP transport for the
// Track 1 / Legal Ops loan-detail surface. Hooks in
// @/hooks/compliance wrap these with TanStack Query.

import { api } from "../client";
import type {
  CaptureMaterialEventRequest,
  ComplianceExceptionDto,
  ComplianceExceptionListQuery,
  EscalateMaterialEventRequest,
  FinancialOpsCheckDto,
  FinancialOpsCheckHistoryDto,
  LegalOpsCheckDto,
  LegalOpsCheckHistoryDto,
  LinkMaterialEventLoanEventRequest,
  MaterialEventDto,
  MaterialEventNoticeEntryDto,
  ReconcileLoanTapeRequest,
  RecordFinancialOpsCheckRequest,
  RecordLegalOpsCheckRequest,
  ResolveComplianceExceptionRequest,
  RouteMaterialEventNoticeRequest,
  SetMaterialEventDispositionRequest,
  VerifyDscrForPeriodRequest,
} from "./types";
import type {
  ComplianceWorkQueueDto,
  DefaultsWorkQueueDto,
  QaWorkQueueDto,
} from "./workQueueTypes";
import type {
  ComplianceIntakeReportDto,
  CreateComplianceIntakeReportRequest,
  EscalateComplianceIntakeReportRequest,
  ResolveComplianceIntakeReportRequest,
  TriageComplianceIntakeReportRequest,
} from "./intakeReportTypes";

export const legalOpsChecksApi = {
  history: (loanId: string) =>
    api.get<LegalOpsCheckHistoryDto>(
      `/compliance/track1/legal/loans/${loanId}/checks`,
    ),
  record: (loanId: string, body: RecordLegalOpsCheckRequest) =>
    api.post<LegalOpsCheckDto>(
      `/compliance/track1/legal/loans/${loanId}/checks`,
      body,
    ),
};

export const materialEventsApi = {
  list: (loanId: string) =>
    api.get<MaterialEventDto[]>(
      `/compliance/track1/legal/loans/${loanId}/material-events`,
    ),
  capture: (loanId: string, body: CaptureMaterialEventRequest) =>
    api.post<MaterialEventDto>(
      `/compliance/track1/legal/loans/${loanId}/material-events`,
      body,
    ),
  get: (id: string) =>
    api.get<MaterialEventDto>(`/compliance/track1/legal/material-events/${id}`),
  notices: (id: string) =>
    api.get<MaterialEventNoticeEntryDto[]>(
      `/compliance/track1/legal/material-events/${id}/notices`,
    ),
  routeNotice: (id: string, body: RouteMaterialEventNoticeRequest) =>
    api.post<MaterialEventNoticeEntryDto>(
      `/compliance/track1/legal/material-events/${id}/notices`,
      body,
    ),
  setDisposition: (id: string, body: SetMaterialEventDispositionRequest) =>
    api.put<MaterialEventDto>(
      `/compliance/track1/legal/material-events/${id}/disposition`,
      body,
    ),
  escalate: (id: string, body: EscalateMaterialEventRequest) =>
    api.post<MaterialEventDto>(
      `/compliance/track1/legal/material-events/${id}/escalate`,
      body,
    ),
  linkLoanEvent: (id: string, body: LinkMaterialEventLoanEventRequest) =>
    api.post<MaterialEventDto>(
      `/compliance/track1/legal/material-events/${id}/link-loan-event`,
      body,
    ),
};

export const financialOpsChecksApi = {
  history: (loanId: string) =>
    api.get<FinancialOpsCheckHistoryDto>(
      `/compliance/track1/financial/loans/${loanId}/checks`,
    ),
  record: (loanId: string, body: RecordFinancialOpsCheckRequest) =>
    api.post<FinancialOpsCheckDto>(
      `/compliance/track1/financial/loans/${loanId}/checks`,
      body,
    ),
  verifyDscr: (loanId: string, body: VerifyDscrForPeriodRequest) =>
    api.post<FinancialOpsCheckDto>(
      `/compliance/track1/financial/loans/${loanId}/dscr/verify`,
      body,
    ),
  reconcileLoanTape: (loanId: string, body: ReconcileLoanTapeRequest) =>
    api.post<FinancialOpsCheckDto>(
      `/compliance/track1/financial/loans/${loanId}/loan-tape/reconcile`,
      body,
    ),
};

export const complianceExceptionsApi = {
  list: (query?: ComplianceExceptionListQuery) =>
    api.get<ComplianceExceptionDto[]>(`/compliance/exceptions`, query),
  get: (id: string) =>
    api.get<ComplianceExceptionDto>(`/compliance/exceptions/${id}`),
  resolve: (id: string, body: ResolveComplianceExceptionRequest) =>
    api.post<ComplianceExceptionDto>(
      `/compliance/exceptions/${id}/resolve`,
      body,
    ),
};

// "Report to compliance" intake — create rides Loans.Read (any staff);
// triage rides Compliance.ManageExceptions; escalate rides Defaults.Manage.
export const intakeReportsApi = {
  listForLoan: (loanId: string) =>
    api.get<ComplianceIntakeReportDto[]>(
      `/compliance/loans/${loanId}/intake-reports`,
    ),
  create: (loanId: string, body: CreateComplianceIntakeReportRequest) =>
    api.post<ComplianceIntakeReportDto>(
      `/compliance/loans/${loanId}/intake-reports`,
      body,
    ),
  get: (id: string) =>
    api.get<ComplianceIntakeReportDto>(`/compliance/intake-reports/${id}`),
  acknowledge: (id: string, body: TriageComplianceIntakeReportRequest) =>
    api.post<ComplianceIntakeReportDto>(
      `/compliance/intake-reports/${id}/acknowledge`,
      body,
    ),
  investigate: (id: string, body: TriageComplianceIntakeReportRequest) =>
    api.post<ComplianceIntakeReportDto>(
      `/compliance/intake-reports/${id}/investigate`,
      body,
    ),
  resolve: (id: string, body: ResolveComplianceIntakeReportRequest) =>
    api.post<ComplianceIntakeReportDto>(
      `/compliance/intake-reports/${id}/resolve`,
      body,
    ),
  escalate: (id: string, body: EscalateComplianceIntakeReportRequest) =>
    api.post<ComplianceIntakeReportDto>(
      `/compliance/intake-reports/${id}/escalate`,
      body,
    ),
};

// Governance cockpits — read-only cross-portfolio work queues.
export const workQueuesApi = {
  compliance: () =>
    api.get<ComplianceWorkQueueDto>(`/compliance/work-queue`),
  qa: () => api.get<QaWorkQueueDto>(`/compliance/qa-work-queue`),
  defaults: () =>
    api.get<DefaultsWorkQueueDto>(`/compliance/defaults-work-queue`),
};
